// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
/**
 * ValidationLoadReport.jsx
 *
 * data-fit LLC — DecisionFlow
 * Stage 1 LOAD — Screen LOAD-02: Validation & Load Report
 * Branch: feature/compliance-lifecycle-foundation
 *
 * Displays server validation results, AI grouped fix cards, and inline
 * cell editing (client-side only). Confirm endpoint receives only
 * bootstrap_batch_id — no edit data is transmitted to the server.
 * To apply corrections server-side, operator downloads corrected CSV
 * and re-uploads, generating a new bootstrap_batch_id.
 *
 * Fix applied (F2): onAiStatusChange + onAiResponseReceived added to
 * props interface. Re-upload handler now calls these to update parent
 * AI state after re-upload validate + AI query complete.
 *
 * Spec authority: ComponentSpec_LOAD_corrected_S86g.md
 * Path: ui/src/components/tenant-admin/ValidationLoadReport.jsx
 */



import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import LoadReportTable from './LoadReportTable';

const API_BASE_URL = '/api/v1';

const FILE_SIZE_LIMIT_MB = 10;
const ROW_LIMIT_PER_FILE = 10000;
const AI_TIMEOUT_MS = 10000;

const FILE_DEFINITIONS = [
  { type: 'company_setup', label: 'Company' },
  { type: 'roles_motions', label: 'Roles' },
  { type: 'positions', label: 'Positions' },
  { type: 'comp_admins', label: 'Admins' },
  { type: 'geo_hierarchy', label: 'Geo' },
  { type: 'fx_rates', label: 'FX' },
];

const TIER_FILES = {
  essentials: ['company_setup', 'roles_motions', 'positions'],
  standard: ['company_setup', 'roles_motions', 'positions', 'comp_admins'],
  enterprise: ['company_setup', 'roles_motions', 'positions', 'comp_admins', 'geo_hierarchy', 'fx_rates'],
};

function normalizeTier(tier) {
  return String(tier || '').toLowerCase();
}

function getTierFiles(tier) {
  return TIER_FILES[normalizeTier(tier)] || TIER_FILES.essentials;
}

function timeoutPromise(ms) {
  return new Promise((_, reject) => {
    window.setTimeout(() => reject(new Error('AI_TIMEOUT')), ms);
  });
}

function buildTopErrors(errors = []) {
  const grouped = errors.reduce((acc, error) => {
    if (!error?.code) return acc;
    acc[error.code] = (acc[error.code] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(grouped)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([code, affected_rows]) => ({ code, affected_rows }));
}

function escapeCsv(value) {
  const normalized = value === null || value === undefined ? '' : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
}

function downloadTextFile({ filename, content, type = 'text/csv;charset=utf-8;' }) {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function getSummary(validationResult, localValidationState) {
  return (
    localValidationState?.summary ||
    validationResult?.summary || {
      total_rows: 0,
      rows_valid: 0,
      rows_warning: 0,
      rows_critical: 0,
    }
  );
}

function Toast({ toast, onDismiss }) {
  if (!toast) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed right-6 top-6 z-50 max-w-md rounded-2xl border px-4 py-3 shadow-lg ${
        toast.type === 'error'
          ? 'border-red-200 bg-red-50 text-red-800'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-sm font-semibold">{toast.type === 'error' ? 'Error' : 'Done'}</span>
        <p className="text-sm">{toast.message}</p>
        <button
          type="button"
          onClick={onDismiss}
          className="ml-2 rounded-md px-1 text-sm font-semibold hover:bg-[rgb(var(--color-surface-muted))]/60 focus:outline-none "
          aria-label="Dismiss notification"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function FullScreenState({ title, message, actionLabel, onAction }) {
  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="max-w-xl rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-sm">
        <h1 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">{title}</h1>
        <p className="mt-3 text-tok-sm leading-6 text-[rgb(var(--color-text-muted))]">{message}</p>
        {actionLabel && (
          <button
            type="button"
            onClick={onAction}
            className="mt-6 rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
          >
            {actionLabel}
          </button>
        )}
      </div>
    </main>
  );
}

function ConfirmDiscardModal({ onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[rgb(var(--color-text))]/50 px-4">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Discard unsaved edits?</h2>
        <p className="mt-3 text-tok-sm leading-6 text-[rgb(var(--color-text-muted))]">
          Your unsaved edits will be lost. Go back?
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
          >
            Stay
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}

function AiSidePanel({ aiStatus, aiResponse, onRetry, dismissedFixCodes, onDismissFix, onApplyFix }) {
  const groupedFixes =
    aiStatus === 'ready' && Array.isArray(aiResponse?.ai_response?.grouped_fixes)
      ? aiResponse.ai_response.grouped_fixes.filter((fix) => !dismissedFixCodes.includes(fix.error_code))
      : [];

  return (
    <aside className="rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
      <div className="mb-4">
        <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">CSV Validator</p>
        <h2 className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-text))]">Active</h2>
      </div>

      {aiStatus === 'loading' && (
        <div className="space-y-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[rgb(var(--color-border))] border-t-[rgb(var(--color-text))]" />
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">I found issues. I can suggest fixes that resolve multiple rows.</p>
        </div>
      )}

      {aiStatus === 'error' && (
        <div className="space-y-3 rounded-2xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4">
          <p className="text-tok-sm text-[rgb(var(--color-danger))]">AI guidance could not be generated.</p>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
          >
            Retry
          </button>
        </div>
      )}

      {aiStatus === 'timeout' && (
        <div className="space-y-3 rounded-2xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-4">
          <p className="text-tok-sm text-[rgb(var(--color-warning))]">AI guidance timed out. You can retry or continue without AI guidance.</p>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
          >
            Retry
          </button>
        </div>
      )}

      {aiStatus === 'blocked' && (
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">AI guidance is unavailable for this request.</p>
        </div>
      )}

      {aiStatus === 'ready' && groupedFixes.length === 0 && (
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No grouped suggestions available.</p>
        </div>
      )}

      {aiStatus === 'ready' && groupedFixes.length > 0 && (
        <div className="space-y-3">
          {groupedFixes.map((fix) => (
            <div key={fix.error_code} className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{fix.error_code}</p>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">{fix.affected_rows} rows affected</p>
              <p className="mt-3 text-tok-sm text-[rgb(var(--color-text))]">{fix.suggestion}</p>
              <p className="mt-2 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                Confidence: {Math.round((fix.confidence || 0) * 100)}%
              </p>
              {fix.basis && <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Basis: {fix.basis}</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onApplyFix(fix)}
                  className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-xs font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2 text-tok-xs font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
                >
                  Preview
                </button>
                <button
                  type="button"
                  onClick={() => onDismissFix(fix.error_code)}
                  className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2 text-tok-xs font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(aiStatus === 'idle' || !aiStatus) && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Grouped AI fixes will appear here when available.</p>
      )}
    </aside>
  );
}

export default function ValidationLoadReport({
  bootstrapBatchId,
  validationResult,
  aiResponse,
  aiStatus,
  tier,
  onLoadConfirmed,
  onBackToUpload,
  onNewBatchId,
  onAiStatusChange,
  onAiResponseReceived,
}) {
  const { token } = useAuth();

  const fileInputRef = useRef(null);
  const warningCheckboxRef = useRef(null);

  const tierFiles = useMemo(() => getTierFiles(tier), [tier]);
  const initialActiveFile = validationResult?.files_received?.find((file) => tierFiles.includes(file)) || tierFiles[0];

  const [activeFile, setActiveFile] = useState(initialActiveFile);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [inMemoryEdits, setInMemoryEdits] = useState({});
  const [localValidationState, setLocalValidationState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [dismissedFixCodes, setDismissedFixCodes] = useState([]);
  const [reuploadError, setReuploadError] = useState(null);

  const summary = getSummary(validationResult, localValidationState);
  const allErrors = localValidationState?.errors || validationResult?.errors || [];

  const activeErrors = useMemo(() => {
    return allErrors
      .filter((error) => error.file === activeFile)
      .filter((error) => severityFilter === 'all' || error.severity === severityFilter);
  }, [activeFile, allErrors, severityFilter]);

  const hasInMemoryEdits = useMemo(() => {
    return Object.values(inMemoryEdits).some((fileEdits) => Object.keys(fileEdits || {}).length > 0);
  }, [inMemoryEdits]);

  const groupedFixes =
    aiStatus === 'ready' && Array.isArray(aiResponse?.ai_response?.grouped_fixes)
      ? aiResponse.ai_response.grouped_fixes.filter((fix) => !dismissedFixCodes.includes(fix.error_code))
      : [];

  const showToast = useCallback((nextToast) => {
    setToast(nextToast);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const recalculateLocalValidation = useCallback(
    (nextEdits) => {
      const originalErrors = validationResult?.errors || [];

      const remainingErrors = originalErrors.filter((error) => {
        const editedValue = nextEdits?.[error.file]?.[error.row]?.[error.column];

        if (editedValue === undefined) {
          return true;
        }

        // OI-BOOTSTRAP-CSV-SPEC: per-column validation rules for {file_type} go here
        // Structure: ({ columnName, value }) => ({ valid: boolean, message: string })
        const isStructurallyFilled = String(editedValue).trim().length > 0;

        return !isStructurallyFilled;
      });

      const resolvedCount = originalErrors.length - remainingErrors.length;
      const nextCritical = remainingErrors.filter((error) => error.severity === 'critical').length;
      const nextWarning = remainingErrors.filter((error) => error.severity === 'warning').length;

      setLocalValidationState({
        errors: remainingErrors,
        summary: {
          ...validationResult.summary,
          rows_critical: nextCritical,
          rows_warning: nextWarning,
          rows_valid: Math.max((validationResult.summary?.rows_valid || 0) + resolvedCount, 0),
        },
      });
    },
    [validationResult]
  );

  const handleEditCell = useCallback(
    (file, rowIndex, column, value) => {
      const nextEdits = {
        ...inMemoryEdits,
        [file]: {
          ...(inMemoryEdits[file] || {}),
          [rowIndex]: {
            ...(inMemoryEdits[file]?.[rowIndex] || {}),
            [column]: value,
          },
        },
      };

      setInMemoryEdits(nextEdits);
      recalculateLocalValidation(nextEdits);
    },
    [inMemoryEdits, recalculateLocalValidation]
  );

  const handleApplyAIFix = useCallback(
    (fix) => {
      const affectedErrors = allErrors.filter((error) => error.code === fix.error_code);
      const nextEdits = { ...inMemoryEdits };

      affectedErrors.forEach((error) => {
        nextEdits[error.file] = {
          ...(nextEdits[error.file] || {}),
          [error.row]: {
            ...(nextEdits[error.file]?.[error.row] || {}),
            [error.column]: fix.suggestion || '',
          },
        };
      });

      setInMemoryEdits(nextEdits);
      recalculateLocalValidation(nextEdits);
      showToast({ type: 'success', message: `${fix.error_code} applied in memory.` });
    },
    [allErrors, inMemoryEdits, recalculateLocalValidation, showToast]
  );

  const handleDismissFix = useCallback((errorCode) => {
    setDismissedFixCodes((current) => Array.from(new Set([...current, errorCode])));
  }, []);

  const handleDownloadErrorReport = useCallback(() => {
    const header = ['file', 'row', 'column', 'severity', 'code', 'message', 'ai_suggestion'];
    const rows = activeErrors.map((error) =>
      [
        error.file,
        error.row,
        error.column,
        error.severity,
        error.code,
        error.message,
        error.ai_suggestion || '',
      ].map(escapeCsv).join(',')
    );

    downloadTextFile({
      filename: `load_errors_${bootstrapBatchId}.csv`,
      content: [header.join(','), ...rows].join('\n'),
    });
  }, [activeErrors, bootstrapBatchId]);

  const handleDownloadCorrectedCsv = useCallback(() => {
    const activeFileErrors = allErrors.filter((error) => error.file === activeFile);
    const editedRows = inMemoryEdits[activeFile] || {};

    const columns = Array.from(new Set(activeFileErrors.map((error) => error.column))).filter(Boolean);

    // OI-BOOTSTRAP-CSV-SPEC: per-column validation rules for {file_type} go here
    // Structure: ({ columnName, value }) => ({ valid: boolean, message: string })
    const header = columns.length > 0 ? columns : ['column', 'value'];

    const rowNumbers = Array.from(
      new Set([
        ...activeFileErrors.map((error) => error.row),
        ...Object.keys(editedRows).map((row) => Number(row)),
      ])
    ).sort((a, b) => a - b);

    const rows = rowNumbers.map((rowNumber) =>
      header
        .map((columnName) => {
          const editedValue = editedRows?.[rowNumber]?.[columnName];
          if (editedValue !== undefined) return escapeCsv(editedValue);

          const sourceError = activeFileErrors.find(
            (error) => error.row === rowNumber && error.column === columnName
          );
          return escapeCsv(sourceError?.message || '');
        })
        .join(',')
    );

    downloadTextFile({
      filename: `${activeFile}_corrected.csv`,
      content: [header.map(escapeCsv).join(','), ...rows].join('\n'),
    });
  }, [activeFile, allErrors, inMemoryEdits]);

  const validateReuploadFile = useCallback(async (file) => {
    if (!file) return { valid: false, message: 'No file selected.' };
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return { valid: false, message: 'File must be .csv format.' };
    }
    if (file.size > FILE_SIZE_LIMIT_MB * 1024 * 1024) {
      return { valid: false, message: `File exceeds maximum size of ${FILE_SIZE_LIMIT_MB} MB.` };
    }

    const text = await file.text();
    const rowCount = text.split(/\r?\n/).filter((line) => line.trim().length > 0).length - 1;

    if (rowCount > ROW_LIMIT_PER_FILE) {
      return { valid: false, message: `File exceeds maximum row limit of ${ROW_LIMIT_PER_FILE}.` };
    }

    return { valid: true, message: null };
  }, []);

  const postAiQueryForValidation = useCallback(
    async (validationPayload) => {
      const response = await Promise.race([
        fetch(`${API_BASE_URL}/ai-queries`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            panel_context: 'load.csv_validation',
            input_data: {
              bootstrap_batch_id: validationPayload.bootstrap_batch_id,
              tier,
              files_received: validationPayload.files_received || [],
              error_summary: {
                critical: validationPayload.summary?.rows_critical || 0,
                warning: validationPayload.summary?.rows_warning || 0,
                valid: validationPayload.summary?.rows_valid || 0,
              },
              top_errors: buildTopErrors(validationPayload.errors || []),
            },
          }),
        }),
        timeoutPromise(AI_TIMEOUT_MS),
      ]);

      if (!response.ok) {
        throw new Error(`AI_QUERY_${response.status}`);
      }

      return response.json();
    },
    [tier, token]
  );

  const retryAi = useCallback(() => {
    if (!validationResult) return;

    if (onAiStatusChange) onAiStatusChange('loading');

    postAiQueryForValidation(validationResult)
      .then((aiPayload) => {
        if (aiPayload && onAiResponseReceived) {
          onAiResponseReceived(aiPayload);
        } else if (onAiStatusChange) {
          onAiStatusChange('error');
        }
      })
      .catch((aiError) => {
        if (onAiStatusChange) {
          onAiStatusChange(aiError.message === 'AI_TIMEOUT' ? 'timeout' : 'error');
        }
      });
  }, [onAiStatusChange, onAiResponseReceived, postAiQueryForValidation, validationResult]);

  const handleReuploadFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      setReuploadError(null);

      const fileCheck = await validateReuploadFile(file);
      if (!fileCheck.valid) {
        setReuploadError(fileCheck.message);
        return;
      }

      setIsLoading(true);

      try {
        const formData = new FormData();
        formData.append(activeFile, file);

        const validateResponse = await fetch(`${API_BASE_URL}/bootstrap-import/validate`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (!validateResponse.ok) {
          let errorPayload = {};
          try {
            errorPayload = await validateResponse.json();
          } catch {
            errorPayload = {};
          }

          setReuploadError(errorPayload.message || 'Re-upload validation failed.');
          return;
        }

        const validationPayload = await validateResponse.json();
        onNewBatchId(validationPayload.bootstrap_batch_id);

        // Re-upload: reset parent AI state then fire new AI query
        if (onAiStatusChange) onAiStatusChange('loading');

        postAiQueryForValidation(validationPayload)
          .then((aiPayload) => {
            if (aiPayload && onAiResponseReceived) {
              onAiResponseReceived(aiPayload);
            } else if (onAiStatusChange) {
              onAiStatusChange('error');
            }
          })
          .catch((aiError) => {
            if (onAiStatusChange) {
              onAiStatusChange(aiError.message === 'AI_TIMEOUT' ? 'timeout' : 'error');
            }
          });

        setInMemoryEdits((current) => ({ ...current, [activeFile]: {} }));
        setLocalValidationState({
          errors: validationPayload.errors || [],
          summary: validationPayload.summary || validationResult.summary,
        });

        showToast({ type: 'success', message: `${file.name} re-uploaded and re-validated.` });
      } catch {
        setReuploadError('Re-upload failed. Try again.');
      } finally {
        setIsLoading(false);
        event.target.value = '';
      }
    },
    [
      activeFile,
      onNewBatchId,
      postAiQueryForValidation,
      showToast,
      token,
      validateReuploadFile,
      validationResult,
    ]
  );

  const handleConfirmLoad = useCallback(async () => {
    setLoadError(null);

    if ((summary.rows_critical || 0) > 0) {
      setLoadError('Resolve all critical errors before loading.');
      return;
    }

    if ((summary.rows_warning || 0) > 0 && !acknowledgedWarnings) {
      warningCheckboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      showToast({ type: 'error', message: 'Please acknowledge warnings to proceed.' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/bootstrap-import/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bootstrap_batch_id: bootstrapBatchId,
          acknowledged_warnings: acknowledgedWarnings,
        }),
      });

      if (!response.ok) {
        let errorPayload = {};
        try {
          errorPayload = await response.json();
        } catch {
          errorPayload = {};
        }

        const code = errorPayload.code || errorPayload.error_code;

        if (code === 'VALIDATION_ID_NOT_FOUND') {
          setLoadError({
            fullScreen: true,
            title: 'Session expired',
            message: 'Session expired. Return to upload and re-validate.',
            actionLabel: 'Return to Upload',
          });
          return;
        }

        if (code === 'VALIDATION_NOT_PASSED') {
          showToast({ type: 'error', message: 'Validation must pass before loading. Check critical errors.' });
          return;
        }

        if (code === 'WARNINGS_NOT_ACKNOWLEDGED') {
          warningCheckboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          showToast({ type: 'error', message: 'Please acknowledge warnings to proceed.' });
          return;
        }

        setLoadError({
          fullScreen: true,
          title: 'Load failed',
          message: `Load failed. Contact support with batch ID ${bootstrapBatchId}.`,
        });
        return;
      }

      const payload = await response.json();
      showToast({ type: 'success', message: 'Load confirmed.' });
      onLoadConfirmed(payload.bootstrap_batch_id || bootstrapBatchId);
    } catch {
      setLoadError({
        fullScreen: true,
        title: 'Load failed',
        message: `Load failed. Contact support with batch ID ${bootstrapBatchId}.`,
      });
    } finally {
      setIsLoading(false);
    }
  }, [acknowledgedWarnings, bootstrapBatchId, onLoadConfirmed, showToast, summary, token]);

  const handleBack = useCallback(() => {
    if (hasInMemoryEdits) {
      setShowDiscardModal(true);
      return;
    }
    onBackToUpload();
  }, [hasInMemoryEdits, onBackToUpload]);

  if (!validationResult || !bootstrapBatchId) {
    return (
      <FullScreenState
        title="Validation result unavailable"
        message="Return to upload and re-validate before continuing."
        actionLabel="Return to Upload"
        onAction={onBackToUpload}
      />
    );
  }

  if (loadError?.fullScreen) {
    return (
      <FullScreenState
        title={loadError.title}
        message={loadError.message}
        actionLabel={loadError.actionLabel}
        onAction={loadError.actionLabel ? onBackToUpload : undefined}
      />
    );
  }

  const canConfirm =
    !isLoading &&
    (summary.rows_critical || 0) === 0 &&
    ((summary.rows_warning || 0) === 0 || acknowledgedWarnings);

  return (
    <main className="mx-auto flex min-h-screen max-w-7xl flex-col px-6 py-6">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {showDiscardModal && (
        <ConfirmDiscardModal
          onCancel={() => setShowDiscardModal(false)}
          onConfirm={() => {
            setShowDiscardModal(false);
            onBackToUpload();
          }}
        />
      )}

      <header className="mb-6 rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">Stage 1</p>
            <h1 className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">Validation Report</h1>
          </div>
          <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-3 py-1 text-tok-sm font-semibold capitalize text-[rgb(var(--color-text))]">
            {validationResult.status || validationResult.validation_status}
          </span>
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <span className="rounded-full bg-[rgb(var(--color-danger))]/10 px-3 py-1 font-semibold text-[rgb(var(--color-danger))]">
            ● {summary.rows_critical || 0} critical
          </span>
          <span className="rounded-full bg-[rgb(var(--color-warning))]/10 px-3 py-1 font-semibold text-[rgb(var(--color-warning))]">
            ◐ {summary.rows_warning || 0} warnings
          </span>
          <span className="rounded-full bg-[rgb(var(--color-success))]/10 px-3 py-1 font-semibold text-[rgb(var(--color-success))]">
            ✓ {summary.rows_valid || 0} valid
          </span>
        </div>
      </header>

      {(summary.rows_critical || 0) > 0 && (
        <div className="mb-6 rounded-2xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm font-medium text-[rgb(var(--color-danger))]">
          Resolve all critical errors before loading.
        </div>
      )}

      {typeof loadError === 'string' && (
        <div className="mb-6 rounded-2xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm font-medium text-[rgb(var(--color-danger))]">
          {loadError}
        </div>
      )}

      <div className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {FILE_DEFINITIONS.filter((file) => tierFiles.includes(file.type)).map((file) => {
                const count = allErrors.filter((error) => error.file === file.type).length;
                const active = activeFile === file.type;

                return (
                  <button
                    key={file.type}
                    type="button"
                    onClick={() => setActiveFile(file.type)}
                    className={`rounded-xl px-3 py-2 text-sm font-semibold focus:outline-none  ${
                      active
                        ? 'bg-slate-950 text-white'
                        : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {file.label}
                    <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">{count}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              className="rounded-xl border border-[rgb(var(--color-border))] px-3 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none"
            >
              Re-upload file
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleReuploadFile}
            />
          </div>

          {reuploadError && (
            <div className="mb-4 rounded-2xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {reuploadError}
            </div>
          )}

          <LoadReportTable
            errors={activeErrors}
            groupedFixes={groupedFixes}
            inMemoryEdits={inMemoryEdits}
            tier={tier}
            onEditCell={handleEditCell}
            onApplyAIFix={handleApplyAIFix}
            onDismissFix={handleDismissFix}
            onSeverityFilter={setSeverityFilter}
            activeFile={activeFile}
            severityFilter={severityFilter}
            summary={summary}
          />

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleDownloadErrorReport}
              className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
            >
              Download error report ↓
            </button>
            <button
              type="button"
              onClick={handleDownloadCorrectedCsv}
              className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
            >
              Download corrected CSV
            </button>
          </div>
        </section>

        <AiSidePanel
          aiStatus={aiStatus}
          aiResponse={aiResponse}
          onRetry={retryAi}
          dismissedFixCodes={dismissedFixCodes}
          onDismissFix={handleDismissFix}
          onApplyFix={handleApplyAIFix}
        />
      </div>

      <footer className="mt-6 rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 shadow-sm">
        {(summary.rows_warning || 0) > 0 && (summary.rows_critical || 0) === 0 && (
          <label
            ref={warningCheckboxRef}
            className="mb-4 flex items-center gap-3 rounded-2xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-3 text-tok-sm font-medium text-[rgb(var(--color-warning))]"
          >
            <input
              type="checkbox"
              checked={acknowledgedWarnings}
              onChange={(event) => setAcknowledgedWarnings(event.target.checked)}
              className="h-4 w-4 rounded border-[rgb(var(--color-border))] focus:outline-none"
            />
            I acknowledge {summary.rows_warning || 0} warnings
          </label>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            disabled={isLoading}
            className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none "
          >
            ← Back
          </button>

          <button
            type="button"
            onClick={handleConfirmLoad}
            disabled={!canConfirm}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:bg-[rgb(var(--color-disabled))] focus:outline-none"
          >
            {isLoading ? 'Loading…' : 'Confirm & Load'}
          </button>
        </div>
      </footer>
    </main>
  );
}
